import React, {useMemo, useState} from "react";
import {Button, CircularProgress, Dialog, DialogActions, DialogContent, IconButton} from "@mui/material";
import {Delete as DeleteIcon, SwapHoriz as SwapHorizontalIcon, SwapVert as SwapVerticalIcon} from "@mui/icons-material";
import {ClosableDialogTitle, VerticalDivider} from "@atttomyx/react-components";
import {ConnectionForm} from "../forms";
import {edgeService} from "../../services";
import {forms, objects, strings, users} from "@atttomyx/shared-utils";
import {confirm} from "@atttomyx/react-utils";

const toData = (edge) => {
    return {
        sourceId: edge.sourceNodeId,
        targetId: edge.targetNodeId,
        category: edge.category,
        label: edge.label,
        valid: true,
    }
};

const EditEdgeDialog = (props) => {
    const { snackbar, horizontal, categories, people, edge, onCancel, onSaveEdge, onDeleteEdge } = props;
    const [ data, setData ] = useState(toData(edge));
    const [ spinner, setSpinner ] = useState(false);

    const idToName = useMemo(() => {
        const map = {};

        people.forEach(person => {
            map[person.id] = users.getFullName(person);
        });

        return map;
    }, [people]);

    const description = useMemo(() => {
        const source = objects.defaultIfNullOrUndefined(idToName[edge.sourceNodeId], "Somebody");
        const target = objects.defaultIfNullOrUndefined(idToName[edge.targetNodeId], "Somebody");
        const connection = objects.defaultIfNullOrUndefined(edge.label, "connection");

        return `${target} is ${strings.possessive(source)} ${connection}`;
    }, [idToName, edge]);

    const failure = (err) => {
        snackbar.setError(err);
        setSpinner(false);
    };

    const deleteEdge = () => {
        setSpinner(true);

        const success = (deletedId) => {
            setSpinner(false);
            onDeleteEdge(deletedId);
        };

        edgeService.deleteEdge(edge.id, success, failure);
    };

    const submitForm = () => {
        setSpinner(true);

        const success = (saved) => {
            setSpinner(false);
            onSaveEdge(saved);
        };

        const modified = {
            sourceNodeId: data.sourceId,
            targetNodeId: data.targetId,
            category: data.category,
            label: data.label,
        };

        edgeService.updateEdge(edge.id, modified, success, failure)
    };

    const swap = () => {
        const modified = objects.deepCopy(data);

        modified.sourceId = data.targetId;
        modified.targetId = data.sourceId;

        setData(modified);
    };

    return <Dialog
        open={true}
        aria-labelledby="edit-edge-dialog"
    >
        <ClosableDialogTitle onClose={onCancel}>
            Edit Connection
        </ClosableDialogTitle>
        <DialogContent className="edit-edge-dialog">
            {spinner ?
                <CircularProgress size="40px"/> :
                <ConnectionForm
                    horizontal={horizontal}
                    categories={categories}
                    people={people}
                    data={data}
                    onChange={setData}
                    allowNew={false}
                    locked={true}
                />}
        </DialogContent>
        {!spinner ? <DialogActions>
            <IconButton
                color="secondary"
                title="Delete"
                className="delete"
                onClick={() => confirm.confirmDelete(`"${description}"`, deleteEdge)}
            >
                <DeleteIcon/>
            </IconButton>
            <IconButton
                color="primary"
                title="Swap positions"
                disabled={!data.sourceId || !data.targetId}
                onClick={swap}
            >
                {horizontal ? <SwapHorizontalIcon/> :<SwapVerticalIcon/>}
            </IconButton>
            <VerticalDivider/>
            <Button
                color="primary"
                disabled={!data.valid || !forms.differ(toData(edge), data)}
                onClick={submitForm}
            >
                Save
            </Button>
        </DialogActions> : null}
    </Dialog>
}

export default EditEdgeDialog;
